import { Label, RadioGroup, RadioGroupItem } from '@commenda/kakashi';
import { FieldTemplateProps } from '@rjsf/utils';
import React, { useEffect, useState } from 'react';

function toBoolString(value: unknown): string {
  if (value === true) return 'true';
  if (value === false) return 'false';
  return '';
}

const BooleanRadioField = ({
  id,
  fieldLabel,
  required,
  rawDescription,
  rawHelp,
  formData,
  onChange,
  disabled,
  errors,
}: {
  id: string;
  fieldLabel: string;
  required: boolean;
  rawDescription?: string;
  rawHelp?: string;
  formData: unknown;
  onChange: (value: boolean | undefined) => void;
  disabled: boolean;
  errors: React.ReactNode;
}) => {
  const [value, setValue] = useState(() => toBoolString(formData));

  useEffect(() => {
    setValue(toBoolString(formData));
  }, [formData]);

  const handleChange = (newValue: string) => {
    setValue(newValue);
    const booleanValue =
      newValue === 'true' ? true : newValue === 'false' ? false : undefined;
    onChange(booleanValue);
  };

  return (
    <div className="mb-4">
      <div className="mb-2">
        <Label htmlFor={id}>
          {fieldLabel}
          {required && <span className="text-text-error ml-1">*</span>}
        </Label>
        {rawDescription && (
          <p className="text-sm text-text-tertiary mt-1">{rawDescription}</p>
        )}
      </div>

      <div className="flex items-center gap-4">
        <RadioGroup
          value={value}
          onValueChange={handleChange}
          disabled={disabled}
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="true" id={`${id}-yes`} />
            <Label htmlFor={`${id}-yes`} className="cursor-pointer">
              Yes
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="false" id={`${id}-no`} />
            <Label htmlFor={`${id}-no`} className="cursor-pointer">
              No
            </Label>
          </div>
        </RadioGroup>
      </div>
      {rawHelp && (
        <p className="text-sm text-text-tertiary mt-1">{rawHelp}</p>
      )}
      {errors}
    </div>
  );
};

export const FieldTemplate = (props: FieldTemplateProps) => {
  const {
    id,
    label,
    schema,
    formData,
    onChange,
    disabled,
    readonly,
    required,
    errors,
    rawDescription,
    rawHelp,
    hidden,
    displayLabel,
    children,
  } = props;

  if (hidden) {
    return null;
  }

  const isBoolean = schema.type === 'boolean';
  const fieldLabel = label || schema.title || '';

  if (isBoolean) {
    return (
      <BooleanRadioField
        id={id}
        fieldLabel={fieldLabel}
        required={required}
        rawDescription={rawDescription}
        rawHelp={rawHelp}
        formData={formData}
        onChange={onChange}
        disabled={disabled || readonly}
        errors={errors}
      />
    );
  }

  return (
    <div className="mb-4">
      <div className="mb-2">
        <Label htmlFor={id}>
          {fieldLabel}
          {required && <span className="text-text-error ml-1">*</span>}
        </Label>
        {rawDescription && (
          <p className="text-sm text-text-tertiary mt-1">{rawDescription}</p>
        )}
      </div>
      {children}
      {rawHelp && <p className="text-sm text-text-tertiary mt-1">{rawHelp}</p>}
      {errors}
    </div>
  );
};
